/**
 * useEgoDevices Hook
 *
 * Fetches the full eGO device list on mount.
 * Returns { devices, isLoading, error }.
 *
 * Requirements: 3.1, 3.4, 3.5
 */

import { useState, useEffect } from 'react';
import { fetchEgoDevices, EgoApiError } from '../api/egoApiClient';
import { useAuth } from '../../auth/useAuth';
import { getAppConfig } from '../../config/appConfig';
import type { EgoDevice } from '../models/egoModels';

export interface UseEgoDevicesResult {
  readonly devices: readonly EgoDevice[];
  readonly isLoading: boolean;
  readonly error: string | null;
}

export function useEgoDevices(): UseEgoDevicesResult {
  const [devices, setDevices] = useState<readonly EgoDevice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { getAccessToken } = useAuth();

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setError(null);

      try {
        const config = getAppConfig();
        const result = await fetchEgoDevices({
          apiHost: config.apiHost,
          getAccessToken,
        });
        if (!cancelled) {
          setDevices(Array.isArray(result) ? result : []);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof EgoApiError
              ? `API error: ${err.status}`
              : 'Failed to load devices'
          );
          setIsLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [getAccessToken]);

  return { devices, isLoading, error };
}
